import React from 'react';
import { Button } from '@global-ecom/nitro-uds/elements';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { tw } from 'twind';

import { useAllCustomerGroupsQuery } from '__generated__/graphql';
import { isServer } from 'utils/constants';

const getDatasetForUrl = () => {
  const host = location.hostname;
  if (host.match('localhost') || host.match('development')) return 'dev';
  else return 'staging';
};

/**
 * Given a timestamp, or using the current time if one's not provided,
 * parse into separate date and time parts.
 */
function getDefaultDateAndTime(timestampInMilliseconds?: string | string[]) {
  const baseDateAndTime = timestampInMilliseconds
    ? new Date(Number.parseInt(timestampInMilliseconds as string))
    : new Date();

  return {
    date: `${baseDateAndTime.getFullYear()}-${(
      baseDateAndTime.getMonth() +
      1 +
      ''
    ).padStart(2, '0')}-${(baseDateAndTime.getDate() + '').padStart(2, '0')}`,
    time: baseDateAndTime.toTimeString().substring(0, 5),
  };
}

const openContentComponentInSanity = e => {
  e.preventDefault();
  const elem = (e.target as HTMLElement).closest('[data-id]') as HTMLElement;
  const data = elem?.dataset;
  if (data?.id && data.type) {
    window.open(
      `https://studio.xetic.puma.com/${getDatasetForUrl()}/desk/` +
        encodeURIComponent(`__edit__${data.id},type=${data.type}`)
    );
  }
};

const ContentManagementToolbox = () => {
  const router = useRouter();
  const [open, setOpen] = React.useState(!!router.query.draft);
  const form = useForm();

  const [customerGroupsResult] = useAllCustomerGroupsQuery({
    pause: isServer,
  });

  const customerGroups = customerGroupsResult.data?.allCustomerGroups;
  const defaultViewAt = getDefaultDateAndTime(router.query.viewAt);

  const callback = React.useCallback(
    event => {
      if (event.data === 'PagePreviewPostMessage') {
        router.push({
          query: { ...router.query, draft: Date.now() },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.query]
  );

  React.useEffect(() => {
    window.addEventListener('message', callback, false);

    return () => window.removeEventListener('message', callback);
  }, [callback, router.query]);

  React.useEffect(() => {
    const cb = openContentComponentInSanity;
    if (open) document.addEventListener('contextmenu', cb);
    else document.removeEventListener('contextmenu', cb);
    return () => document.removeEventListener('contextmenu', cb);
  }, [open]);

  const handleSubmit = data => {
    const selectedDate =
      data.date && data.time
        ? new Date(`${data.date}T${data.time}`)
        : undefined;

    const query = Object.entries({
      ...router.query,
      customerGroups: data.customerGroup ? [data.customerGroup] : undefined,
      draft: Date.now(),
      global: data.global,
      src: data.sourceCode ? data.sourceCode : undefined,
      viewAt: selectedDate?.getTime(),
    }).reduce((acc, [k, v]) => {
      if (v !== undefined) acc[k] = v;
      return acc;
    }, {});

    router.push({ query });
  };

  const clearForm = e => {
    e.preventDefault();
    form.reset();
    router.push({ pathname: router.asPath.split('?')[0] });
  };

  return (
    <>
      {open && (
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          noValidate
          className={tw(
            'fixed z-40 w-full max-w-80 bottom-14 right-8 p-6 bg-white border shadow-2xl rounded-xl space-y-3'
          )}
        >
          <label className="block space-y-2 uppercase font-bold text-sm">
            <span>View Page On</span>
            <div
              data-test-id="content-management-toolbox"
              className="flex border divide-x rounded-sm"
            >
              <input
                ref={form.register()}
                name="date"
                defaultValue={defaultViewAt.date}
                className="w-full p-2"
                type="date"
              />
              <input
                ref={form.register()}
                name="time"
                defaultValue={defaultViewAt.time}
                className="w-full p-2"
                type="time"
                step="60"
              />
            </div>
          </label>
          <label className="block space-y-2 uppercase font-bold text-sm">
            <span>Customer Group</span>
            <div className="w-full border p-2">
              <select
                ref={form.register()}
                name="customerGroup"
                className="w-full"
              >
                <option value="">Select Customer Group</option>
                {customerGroups
                  ? customerGroups.map(group => (
                      <option
                        key={group}
                        selected={router.query.customerGroups === group}
                      >
                        {group}
                      </option>
                    ))
                  : 'Loading...'}
              </select>
            </div>
          </label>
          <label className="block space-y-2 uppercase font-bold text-sm">
            <span>Source Code</span>
            <input
              ref={form.register()}
              defaultValue={router.query.src}
              name="sourceCode"
              className="block w-full border p-2"
              type="text"
            />
          </label>
          <label className="block space-y-2 uppercase font-bold text-sm cursor-pointer">
            <span>Global Content</span>
            <div className="w-full border p-3 flex items-center space-x-3">
              <input
                className="cursor-pointer"
                type="checkbox"
                name="global"
                defaultChecked={router.query.global === 'true'}
                ref={form.register()}
              />
              <span className="font-normal normal-case leading-none block mb-0.5">
                See page from a global context
              </span>
            </div>
          </label>
          <div className="flex space-x-2 !mt-6">
            <Button
              type="submit"
              label="View"
              dataTestId="content-management-submit-btn"
            />
            <Button
              onClick={clearForm}
              variant="secondary"
              label="Clear"
              dataTestId="content-management-clear-btn"
            />
          </div>
        </form>
      )}
      <button
        onClick={() => setOpen(!open)}
        data-test-id="content-management-dialog"
        aria-label="Content management dialog"
        className="fixed z-50 bottom-10 right-5 bg-black text-white flex rounded-full m-0 p-2"
        type="button"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          enableBackground="new 0 0 24 24"
          height="24px"
          viewBox="0 0 24 24"
          width="24px"
          fill="currentColor"
        >
          <g>
            <rect fill="none" height="24" width="24" />
          </g>
          <g>
            <g>
              <rect
                height="8.48"
                transform="matrix(0.7071 -0.7071 0.7071 0.7071 -6.8717 17.6255)"
                width="3"
                x="16.34"
                y="12.87"
              />
              <path d="M17.5,10c1.93,0,3.5-1.57,3.5-3.5c0-0.58-0.16-1.12-0.41-1.6l-2.7,2.7L16.4,6.11l2.7-2.7C18.62,3.16,18.08,3,17.5,3 C15.57,3,14,4.57,14,6.5c0,0.41,0.08,0.8,0.21,1.16l-1.85,1.85l-1.78-1.78l0.71-0.71L9.88,5.61L12,3.49 c-1.17-1.17-3.07-1.17-4.24,0L4.22,7.03l1.41,1.41H2.81L2.1,9.15l3.54,3.54l0.71-0.71V9.15l1.41,1.41l0.71-0.71l1.78,1.78 l-7.41,7.41l2.12,2.12L16.34,9.79C16.7,9.92,17.09,10,17.5,10z" />
            </g>
          </g>
        </svg>
      </button>
    </>
  );
};

export default ContentManagementToolbox;
